import { FC, useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { FiltersActionsApply, useTableDefsRidesMap } from "@ryde/controls";
import {
  FinalSchemaService,
  PageHeader,
  PageHeaderFilters,
  TableSettingsDrawerWrapperGeneric,
} from "@ryde/layout";
import {
  PageHeaderContext,
  useInitViewsAndFilters,
  usePageContext,
} from "@ryde/redux";
import {
  Log,
  tableSettingsDrawerAF,
  topCalendarDatesAtomFamily,
  type IRideTableDto,
} from "@ryde/shared";

import { LiveMapContext } from "./context";
import {
  LiveMapHoursRangeWidget,
  LiveMapMobileStatLeftPanel,
  LiveMapPageBodyStyled,
  LiveMapPageInner,
  LiveMapPageShellStyled,
  LiveMapTop,
} from "./panels";

export const LiveMapPageContent: FC = () => {
  const { entity } = usePageContext();
  const { t } = useTranslation();
  const calendarDates = useRecoilValue(topCalendarDatesAtomFamily(entity));
  const { tableDefsRidesMap } = useTableDefsRidesMap();
  const tableSettingsDrawerOpened = useRecoilValue(
    tableSettingsDrawerAF(entity),
  );

  // The grid on this page waits on the table filters, so they must be initialized exactly once the view
  // request IDs are there — and even when the views request itself fails.
  useInitViewsAndFilters({ entity, searchQueryToOptimize: "rides" });
  const { fetchRidesWithLoader } = useContext(LiveMapContext);

  useEffect(() => {
    if (calendarDates) {
      Log.debug(`FETCH RIDES CALLED, for dates:`, calendarDates);
      fetchRidesWithLoader?.();
    }
  }, [calendarDates, fetchRidesWithLoader]);

  const onFiltersApply = useEvent((): void => {
    fetchRidesWithLoader?.();
  });

  const rideMapHeaderContextValue = useMemo(() => {
    return {
      onFiltersApply: onFiltersApply,
      onFiltersClear: onFiltersApply,
      onSearchApply: onFiltersApply,
    };
  }, [onFiltersApply]);

  return (
    <>
      <FinalSchemaService />
      <LiveMapPageShellStyled>
        <PageHeader
          mainTitle={t("rideMap.plural")}
          topWidgetComponent={<LiveMapHoursRangeWidget entity={entity} />}
          bottomWidgetComponent={<LiveMapTop />}
          mobileStatLeftComponent={<LiveMapMobileStatLeftPanel />}
        >
          <PageHeaderContext.Provider value={rideMapHeaderContextValue}>
            <PageHeaderFilters Actions={<FiltersActionsApply />} />
          </PageHeaderContext.Provider>
        </PageHeader>

        <LiveMapPageBodyStyled>
          <LiveMapPageInner />
        </LiveMapPageBodyStyled>
      </LiveMapPageShellStyled>
      {tableSettingsDrawerOpened && (
        <TableSettingsDrawerWrapperGeneric<IRideTableDto>
          tableDefs={tableDefsRidesMap}
        />
      )}
      {/*  added for future implementation of functions
     {bulkOperationsDrawerOpened && <LiveMapOperationsDrawer />}
      <LiveMapFloatingMenu /> */}
    </>
  );
};
